import React from "react";

import { useTranslation } from "react-i18next";

import {
  PaymentMethodForm,
  useSetupIntentReturn,
} from "@/components/billing/payment-method-form";
import { PaymentMethodSummary } from "@/components/billing/payment-method-summary";
import Button from "@/components/button";
import { useBillingStatusQuery } from "@/queries/hooks/billingHooks";
import type { BillingStatus } from "@/schemas/billing";
import { notify } from "@/utils";
import { formatBillingDate, trialEndFromNow } from "@/utils/billing";

import { useOwnerOnboarding } from "../-owner-onboarding-context";

interface Props {
  onContinue?: (handler: () => Promise<boolean>) => void;
}

/**
 * Owner-onboarding billing step (only rendered when VITE_SUBSCRIPTION_BILLING
 * is on AND the account is opted in, i.e. billing/status.enabled). Collects a card or US bank account via the Stripe Payment Element
 * (SetupIntent); nothing is charged here — `owner-onboarding/me/complete`
 * starts the subscription, with the free trial when one applies.
 */
export function OwnerBillingStep({ onContinue }: Props) {
  const { t } = useTranslation("billing");
  const { data, patch } = useOwnerOnboarding();
  const statusQuery = useBillingStatusQuery();
  const status = statusQuery.data;
  const [changing, setChanging] = React.useState(false);

  const applySaved = React.useCallback(
    (s: BillingStatus) => {
      patch({
        billingConfigured: Boolean(s.paymentMethod) || s.paymentMethodPending,
        cardBrand: s.paymentMethod?.brand ?? undefined,
        cardLast4: s.paymentMethod?.last4 ?? undefined,
      });
      setChanging(false);
    },
    [patch],
  );

  const { pending: returnPending } = useSetupIntentReturn({
    onConfirmed: applySaved,
  });

  const configured =
    data.billingConfigured ||
    Boolean(status?.paymentMethod) ||
    Boolean(status?.paymentMethodPending);
  const showForm = !configured || changing;

  // The wizard's Continue calls whatever handler is registered. Delegate to a
  // ref so the form can (re)register its submit whenever it becomes ready.
  const formSubmitRef = React.useRef<(() => Promise<boolean>) | null>(null);
  const registerSubmit = React.useCallback(
    (fn: (() => Promise<boolean>) | null) => {
      formSubmitRef.current = fn;
    },
    [],
  );
  const showFormRef = React.useRef(showForm);
  showFormRef.current = showForm;
  const busyRef = React.useRef(returnPending);
  busyRef.current = returnPending;

  const handleContinue = React.useCallback(async () => {
    if (busyRef.current) {
      notify.info(t("form.loading"));
      return false;
    }
    if (!showFormRef.current) return true;
    const submit = formSubmitRef.current;
    if (!submit) {
      notify.error(t("form.notReady"));
      return false;
    }
    return submit();
  }, [t]);

  // No deps on purpose: the Wizard resets its handler after a step change
  // (its effect runs after ours), so re-register on every render.
  React.useEffect(() => {
    onContinue?.(handleContinue);
  });

  const trialDays = status?.trialDays ?? 0;
  const trialEnd = formatBillingDate(
    trialEndFromNow(trialDays).toISOString(),
  );

  const summary = status?.paymentMethod
    ? status.paymentMethod
    : data.billingConfigured
      ? {
          type: "card" as const,
          brand: data.cardBrand ?? null,
          last4: data.cardLast4 ?? null,
        }
      : null;

  return (
    <section className="w-full flex flex-col gap-5">
      <div>
        <h2 className="heading-1">
          {configured && !changing
            ? t("onboarding.savedTitle")
            : t("onboarding.addTitle")}
        </h2>
        <p className="body-small text-gray-600 mt-2">
          {trialDays > 0
            ? t("onboarding.trialCopy", { count: trialDays, date: trialEnd })
            : t("onboarding.noTrialCopy")}
        </p>
      </div>

      {returnPending ? (
        <p className="body-small text-gray-500">{t("form.confirming")}</p>
      ) : showForm ? (
        <div className="flex flex-col gap-3 max-w-[520px]">
          <PaymentMethodForm
            returnUrl={window.location.origin + window.location.pathname}
            registerSubmit={registerSubmit}
            onSaved={applySaved}
          />
          {configured ? (
            <div>
              <Button
                type="button"
                variant="link"
                onClick={() => setChanging(false)}
              >
                {t("form.keepCurrent")}
              </Button>
            </div>
          ) : null}
        </div>
      ) : summary ? (
        <PaymentMethodSummary
          paymentMethod={summary}
          pending={status?.paymentMethodPending}
          action={
            <Button
              type="button"
              variant="link"
              onClick={() => setChanging(true)}
            >
              {t("paymentMethod.change")}
            </Button>
          }
        />
      ) : status?.paymentMethodPending ? (
        <p className="body-small text-gray-600">
          {t("paymentMethod.pendingVerification")}
        </p>
      ) : null}
    </section>
  );
}
